import { useState, useMemo, useCallback, useEffect } from 'react';
import { DndContext, DragOverlay, PointerSensor, useSensor, useSensors } from '@dnd-kit/core';
import type { DragStartEvent, DragEndEvent, DragMoveEvent } from '@dnd-kit/core';
import { ChevronLeft, ChevronRight, Plus, Search, UserPlus } from 'lucide-react';
import { useSearchParams } from 'react-router-dom';
import { palette, admin } from '../../styles/tokens';
import { useAuthStore } from '../../stores/authStore';
import { useIndustryLens } from '../../hooks/useIndustryLens';
import { useScheduleEntries } from '../../hooks/useScheduleEntries';
import { useCreatePublicBooking } from '../../hooks/useCreatePublicBooking';
import { useResolvedClusterConfig } from '../../hooks/useResolvedClusterConfig';
import { useBusinessSchedulePreferences } from '../../hooks/useBusinessSchedulePreferences';
import { useScheduleVocabulary } from '../../hooks/useScheduleVocabulary';
import { resolveTravelBuffer } from '../../lib/scheduleTravelBuffer';
import { lookupErrorCopy } from '../../lib/errorMap';
import { useScheduleDragDrop } from './hooks';
import { useDayCanvasDispatcher } from './walkInHandlers';
import { detectConflicts, getUnassignedEntries } from './config';
import { UnassignedBar } from './UnassignedBar';
import { WalkInQueueCard } from '../../components/schedule/WalkInQueueCard';
import { ConflictBanner } from './ConflictBanner';
import { DragOverlayCard } from './DragOverlayCard';
import { AppointmentDetailPanel } from './AppointmentDetailPanel';
import { WalkInModal } from './WalkInModal';
import { AddToSchedulePanel } from '../../components/schedule/AddToSchedulePanel';
import { EditEntryPanel } from '../../components/schedule/EditEntryPanel';
import { Toast } from '../../components/common/Toast';
import { KeyboardShortcutHelp, type ShortcutItem } from '../../components/common/KeyboardShortcutHelp';
import { ZoomPill, type ZoomPillValue } from '../../components/schedule/ZoomPill';
import { LensPill, type LensPillValue } from '../../components/schedule/LensPill';
import { TodayPill } from '../../components/schedule/TodayPill';
import { ScheduleSearchSheet } from '../../components/schedule/ScheduleSearchSheet';
import { useScheduleUrlState } from '../../hooks/useScheduleUrlState';
import type { BookingSource } from '../../data/enums';
import { supabase } from '../../lib/supabase';
import type { ScheduleEntry } from '../../types';
import { ScheduleMainCanvas } from './ScheduleMainCanvas';
import { formatDateRange } from './viewStateHelpers';
import { useScheduleViewChrome } from './useScheduleViewChrome';

export function SchedulePage() {
  const business = useAuthStore(s => s.business);
  const user = useAuthStore(s => s.user);
  const { isLensActive, scheduleConfig, clusterKey, categoryId } = useIndustryLens();

  const isAppointmentCluster = clusterKey === 'A' || clusterKey === 'F';

  const [bizHours, setBizHours] = useState({ open_time: '09:00', close_time: '17:00' });
  useEffect(() => {
    if (!isAppointmentCluster || !business?.id) return;
    const dow = new Date().getDay();
    supabase.from('business_hours').select('open_time, close_time')
      .eq('business_id', business.id).eq('day_of_week', dow).eq('is_open', true).maybeSingle()
      .then(({ data }) => { if (data) setBizHours({ open_time: data.open_time, close_time: data.close_time }); });
  }, [isAppointmentCluster, business?.id]);

  const {
    zoom: zoomParam,
    lens: lensParam,
    date: dateParam,
    setZoom,
    setLens,
    setDate,
  } = useScheduleUrlState({
    fallbackZoom: 'day',
    fallbackLens: 'calendar',
  });

  const { config: clusterConfig } = useResolvedClusterConfig(categoryId);

  const routeLensEnabled = clusterConfig.showRouteLens;
  const { zoom, lens, viewMode, zoomOptions, lensOptions } = useScheduleViewChrome({
    userId: user?.id,
    isLensActive,
    scheduleConfig,
    routeLensEnabled,
    zoomParam: zoomParam,
    lensParam: lensParam,
    setZoom: (next) => setZoom(next),
    setLens: (next) => setLens(next),
  });

  const [anchorDate, setAnchorDate] = useState<Date>(() => new Date());
  useEffect(() => {
    const parsed = new Date(`${dateParam}T12:00:00`);
    if (!Number.isNaN(parsed.getTime())) setAnchorDate(parsed);
  }, [dateParam]);
  const [addPanel, setAddPanel] = useState(false);
  const [addPreselect, setAddPreselect] = useState<string | null>(null);
  const [editEntry, setEditEntry] = useState<ScheduleEntry | null>(null);
  const [toast, setToast] = useState<{ msg: string; type: 'success' | 'error' } | null>(null);
  const [dragEntry, setDragEntry] = useState<ScheduleEntry | null>(null);
  const [timePreview, setTimePreview] = useState<string | null>(null);
  const [detailEntry, setDetailEntry] = useState<ScheduleEntry | null>(null);
  const [walkInOpen, setWalkInOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);

  const [services, setServices] = useState<{ id: string; name: string; duration_minutes: number; price: number }[]>([]);
  useEffect(() => {
    if (!isAppointmentCluster || !business?.id) return;
    supabase.from('services').select('id, name, duration_minutes, price')
      .eq('business_id', business.id).eq('is_active', true).order('sort_order')
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      .then(({ data }) => { if (data) setServices(data as any); });
  }, [isAppointmentCluster, business?.id]);

  const [searchParams, setSearchParams] = useSearchParams();

  useEffect(() => { document.title = isAppointmentCluster ? 'CrewBit · Bookings' : 'CrewBit · Schedule'; }, [isAppointmentCluster]);

  /* eslint-disable react-hooks/exhaustive-deps */
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.target instanceof HTMLInputElement || e.target instanceof HTMLTextAreaElement || e.target instanceof HTMLSelectElement) return;
      if (e.key === 't' || e.key === 'T') { goToToday(); e.preventDefault(); }
      else if (e.key === '/' && !e.metaKey && !e.ctrlKey) { setSearchOpen(true); e.preventDefault(); }
      else if (e.key === 'ArrowLeft') { nav(-1); e.preventDefault(); }
      else if (e.key === 'ArrowRight') { nav(1); e.preventDefault(); }
      else if (e.key === 'Escape') { setDetailEntry(null); setWalkInOpen(false); setEditEntry(null); }
      else if (isAppointmentCluster && (e.key === 'n' || e.key === 'N') && !e.metaKey && !e.ctrlKey) { setWalkInOpen(true); e.preventDefault(); }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [isAppointmentCluster]); // nav and goToToday are stable via useCallback
  /* eslint-enable react-hooks/exhaustive-deps */
  useEffect(() => {
    const a = searchParams.get('action');
    if (!a) return;
    const next = new URLSearchParams(searchParams);
    next.delete('action');
    setSearchParams(next);
    if (a === 'add-to-schedule') { setAddPreselect(null); setAddPanel(true); }
    else if (a === 'new-shift') { setAddPreselect('shift'); setAddPanel(true); }
    else if (a === 'new-job') { setAddPreselect('job'); setAddPanel(true); }
    else if (a === 'new-booking') { setAddPreselect('booking'); setAddPanel(true); }
  }, [searchParams, setSearchParams]);

  const effectiveAnchor = useMemo(() => {
    if (viewMode === 'month') return new Date(anchorDate.getFullYear(), anchorDate.getMonth(), 1);
    if (viewMode === 'year') return new Date(anchorDate.getFullYear(), 0, 1);
    return anchorDate;
  }, [anchorDate, viewMode]);

  const queryMode = viewMode === 'team' || viewMode === 'year' ? 'day' : viewMode;
  const { entries, users, loading, error, refresh } =
    useScheduleEntries(business?.id, effectiveAnchor, queryMode as 'day' | 'week' | 'month');
  const handleScheduleError = useCallback((msg: string) => setToast({ msg, type: 'error' }), []);
  const { handleEntryDrop, handleDateDrop } = useScheduleDragDrop(business?.id, refresh, handleScheduleError);
  const createPublicBooking = useCreatePublicBooking();

  const handleAppointmentBook = useCallback(async (data: {
    clientName: string; serviceId: string; serviceName: string;
    operatorId: string; startTime: string; endTime: string; source?: string;
  }) => {
    if (!business?.id || !user?.id) return;
    const ds = anchorDate.toISOString().split('T')[0];
    const source: 'app' | 'phone' | 'walk_in' = data.source === 'walk_in' ? 'walk_in' : 'phone';
    const result = await createPublicBooking({
      businessId: business.id,
      serviceId: data.serviceId,
      date: ds,
      startTime: data.startTime,
      clientName: data.clientName,
      clientPhone: '',
      operatorId: data.operatorId !== '__solo' ? data.operatorId : undefined,
      source,
      idempotencyKey: crypto.randomUUID(),
    });
    if (!result.success) {
      setToast({ msg: lookupErrorCopy('create_public_booking', result.error.code), type: 'error' });
      return;
    }
    refresh();
    setToast({ msg: `Booked: ${data.clientName} — ${data.serviceName}`, type: 'success' });
  }, [business?.id, user?.id, anchorDate, createPublicBooking, refresh]);

  const handleAppointmentAdvance = useCallback(async (_entryId: string, bookingId: string, status: string) => {
    if (!business?.id || !user?.id) return;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const updates: Record<string, any> = { status };
    if (status === 'completed') updates.completed_at = new Date().toISOString();
    await supabase.from('bookings').update(updates).eq('id', bookingId);
    await supabase.from('activity_log').insert({
      business_id: business.id, entity_type: 'booking', entity_id: bookingId,
      activity_type: `booking_${status}`, description: `Status → ${status}`, logged_by: user.id,
    });
    refresh();
    setToast({ msg: `Status updated to ${status}`, type: 'success' });
    setDetailEntry(null);
  }, [business?.id, user?.id, refresh]);

  const otThreshold = business?.ot_warning_threshold ?? 44;
  const conflicts = useMemo(() => detectConflicts(entries, users, otThreshold), [entries, users, otThreshold]);
  const unassigned = useMemo(() => getUnassignedEntries(entries), [entries]);

  const businessTier = (business as { tier?: string } | null)?.tier ?? null;
  const businessTravelBufferMinutes = (business as { travel_buffer_minutes?: number | null } | null)?.travel_buffer_minutes ?? null;
  const { data: businessSchedulePreferences } = useBusinessSchedulePreferences();
  // resolveDayLayout only reads `staff_columns` (never set pre-launch); DayCanvas
  // does not consume `recurring_badge` — pass undefined (mirrors mobile Phase 1c).
  const dayCanvasFeatureFlags = undefined;
  const showWalkInQueue = isAppointmentCluster && businessSchedulePreferences?.walk_in_enabled === true && Boolean(business?.id);
  const vocab = useScheduleVocabulary(business?.industry ?? null);
  const dayCanvasTravelBuffer = resolveTravelBuffer({
    urlParamRaw: searchParams.get('travel_buffer'),
    userPref: null,
    businessOverride: businessTravelBufferMinutes,
    industryDefault: null,
  });
  const dayCanvasDispatcher = useDayCanvasDispatcher({ refresh, setToast });
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 5 } }));
  const uid = user?.id ?? '';

  const onDragStart = useCallback((event: DragStartEvent) => {
    const entry = (event.active.data.current as { entry?: ScheduleEntry })?.entry ?? entries.find(e => e.id === event.active.id) ?? null;
    setDragEntry(entry);
    setTimePreview(null);
  }, [entries]);

  const onDragMove = useCallback((_event: DragMoveEvent) => {
    setTimePreview(null);
  }, []);

  const onDragEnd = useCallback(async (event: DragEndEvent) => {
    setDragEntry(null);
    setTimePreview(null);
    const { active, over } = event;
    if (!over || !active || !uid) return;
    const entryId = String(active.id);
    const overId = String(over.id);

    const dragKind = (active.data.current as { kind?: string } | undefined)?.kind;
    if (dragKind === 'walk_in') {
      let operatorId = '';
      if (overId.startsWith('day-col-')) operatorId = overId.replace('day-col-', '');
      else if (overId.startsWith('grid-col-')) operatorId = overId.replace('grid-col-', '');
      if (!operatorId || operatorId === '__unassigned') return;
      await dayCanvasDispatcher.handleWalkInDrop(entryId, operatorId, effectiveAnchor);
      return;
    }

    if (overId.startsWith('team-row-')) {
      const newUserId = overId.replace('team-row-', '');
      if (!newUserId || newUserId === '__unassigned') return;
      const ok = await handleEntryDrop(entryId, newUserId, uid);
      if (ok) setToast({ msg: 'Entry reassigned', type: 'success' });
    }
    else if (overId.startsWith('day-col-')) {
      const newUserId = overId.replace('day-col-', '');
      if (newUserId === '__unassigned') return;
      const ok = await handleEntryDrop(entryId, newUserId, uid);
      if (ok) setToast({ msg: 'Entry reassigned', type: 'success' });
    }
    else if (overId.startsWith('week|')) {
      const parts = overId.split('|');
      const newUserId = parts[1];
      const newDate = parts[2];
      const entry = entries.find(e => e.id === entryId);
      const oldDate = entry?.start_at.split('T')[0];
      const oldUser = entry?.assigned_to?.[0];
      if (newDate && newDate !== oldDate) {
        const ok = await handleDateDrop(entryId, newDate, uid);
        if (ok) setToast({ msg: 'Entry moved', type: 'success' });
      }
      if (newUserId && newUserId !== '__unassigned' && newUserId !== oldUser) {
        const ok = await handleEntryDrop(entryId, newUserId, uid);
        if (ok) setToast({ msg: 'Entry reassigned', type: 'success' });
      }
    }
    else if (overId.startsWith('grid-col-')) {
      const newUserId = overId.replace('grid-col-', '');
      const entry = entries.find(e => e.id === entryId);
      const oldUser = entry?.assigned_to?.[0];
      if (newUserId && newUserId !== oldUser) {
        const ok = await handleEntryDrop(entryId, newUserId, uid);
        if (ok) setToast({ msg: 'Appointment reassigned', type: 'success' });
      }
    }
  }, [uid, entries, handleEntryDrop, handleDateDrop, dayCanvasDispatcher, effectiveAnchor]);

  const nav = useCallback((dir: -1 | 1) => {
    const d = new Date(anchorDate);
    if (viewMode === 'month') d.setMonth(d.getMonth() + dir);
    else if (viewMode === 'year') d.setFullYear(d.getFullYear() + dir);
    else if (viewMode === 'week' || viewMode === 'team') d.setDate(d.getDate() + (7 * dir));
    else d.setDate(d.getDate() + dir);
    setAnchorDate(d);
    setDate(d.toISOString().split('T')[0] ?? '');
  }, [anchorDate, viewMode, setDate]);

  const goToToday = useCallback(() => {
    const today = new Date();
    setAnchorDate(today);
    setDate(today.toISOString().split('T')[0] ?? '');
  }, [setDate]);

  const handleDayClick = useCallback((date: Date) => {
    setAnchorDate(date);
    setDate(date.toISOString().split('T')[0] ?? '');
    setZoom('day');
    setLens('calendar');
  }, [setDate, setZoom, setLens]);

  const setLensInUrl = useCallback((nextLens: 'calendar' | 'crew' | 'route') => {
    setZoom('day');
    setLens(nextLens);
  }, [setZoom, setLens]);

  const handleZoomChange = useCallback((nextZoom: ZoomPillValue) => {
    setZoom(nextZoom);
    if (nextZoom === 'month') {
      setAnchorDate((prev) => new Date(prev.getFullYear(), prev.getMonth(), 1));
    }
  }, [setZoom]);

  const handleLensChange = useCallback((nextLens: LensPillValue) => {
    setLens(nextLens);
  }, [setLens]);

  const shortcuts = useMemo<ShortcutItem[]>(() => [
    { key: 't', description: 'Jump to today' },
    { key: '←', description: 'Previous period' },
    { key: '→', description: 'Next period' },
    { key: 'n', description: 'New walk-in' },
    { key: '/', description: 'Open schedule search' },
    { key: 'Esc', description: 'Close open panel' },
    { key: '?', description: 'Show shortcuts' },
  ], []);

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 20 }}>
        <div>
          <div style={{ fontSize: 24, fontWeight: 700, color: admin.header, letterSpacing: '-0.5px' }}>Schedule</div>
          <div style={{ fontSize: 13.5, color: admin.caption, marginTop: 4 }}>
            {viewMode === 'team'
              ? 'Crew lens — drag to reassign'
              : viewMode === 'year'
                ? 'Year zoom — density overview'
                : viewMode === 'week'
                  ? 'Week zoom — drag to reschedule'
                  : viewMode === 'day'
                    ? `Day zoom${isLensActive ? ` — ${scheduleConfig.slotDuration}min slots` : ''} — drag to reassign`
                    : 'Month zoom overview'}
          </div>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          {isAppointmentCluster && (
            <button onClick={() => setWalkInOpen(true)} style={{ display: 'flex', alignItems: 'center', gap: 4, padding: '7px 14px', borderRadius: 20, border: 'none', background: palette.accent, fontSize: 13, fontWeight: 600, color: palette.white, cursor: 'pointer', fontFamily: 'inherit' }}><UserPlus size={14} /> Walk-in</button>
          )}
          <button onClick={() => { setAddPreselect(null); setAddPanel(true); }} style={{ display: 'flex', alignItems: 'center', gap: 4, padding: '7px 14px', borderRadius: 8, border: 'none', background: isAppointmentCluster ? palette.g100 : palette.accent, fontSize: 13, fontWeight: 600, color: isAppointmentCluster ? palette.g700 : palette.white, cursor: 'pointer', fontFamily: 'inherit' }}><Plus size={15} /> {vocab.primaryAction}</button>
        </div>
      </div>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16, flexWrap: 'wrap', gap: 10 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <button onClick={() => nav(-1)} style={{ width: 32, height: 32, borderRadius: 6, border: `1px solid ${palette.g200}`, background: palette.white, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}><ChevronLeft size={16} color={palette.g600} /></button>
          <button onClick={() => nav(1)} style={{ width: 32, height: 32, borderRadius: 6, border: `1px solid ${palette.g200}`, background: palette.white, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}><ChevronRight size={16} color={palette.g600} /></button>
          <span style={{ fontSize: 15, fontWeight: 600, color: palette.g800, marginLeft: 4 }}>
            {formatDateRange(effectiveAnchor, zoom, lens)}
          </span>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <ZoomPill value={zoom} options={zoomOptions} onChange={handleZoomChange} />
          <LensPill value={lens} options={lensOptions} onChange={handleLensChange} />
          <button onClick={() => setSearchOpen(true)} style={{ marginLeft: 6, width: 30, height: 30, borderRadius: 999, border: `1px solid ${palette.g200}`, background: palette.white, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <Search size={14} color={palette.g600} />
          </button>
        </div>
      </div>

      <DndContext sensors={sensors} onDragStart={onDragStart} onDragMove={onDragMove} onDragEnd={onDragEnd}>
        {showWalkInQueue && business?.id ? (
          <WalkInQueueCard businessId={business.id} onAddSuccess={() => refresh()} />
        ) : null}
        <UnassignedBar entries={unassigned} onEntryClick={setEditEntry} />
        <ConflictBanner conflicts={conflicts} />
        <ScheduleMainCanvas
          loading={loading}
          error={error}
          entries={entries}
          viewMode={viewMode}
          isAppointmentCluster={isAppointmentCluster}
          emptyMessage={vocab.emptyMessage}
          primaryAction={vocab.primaryAction}
          onCreate={() => { setAddPreselect(null); setAddPanel(true); }}
          effectiveAnchor={effectiveAnchor}
          bizHours={bizHours}
          onPrev={() => nav(-1)}
          onNext={() => nav(1)}
          onToday={goToToday}
          refresh={refresh}
          onToast={setToast}
          users={users}
          onEntryTap={setDetailEntry}
          onEntryEdit={setEditEntry}
          business={business as { id?: string; industry?: string | null; tier?: string | null } | null}
          categoryId={categoryId}
          dayCanvasFeatureFlags={dayCanvasFeatureFlags}
          businessTier={businessTier}
          lens={lens}
          setLensInUrl={setLensInUrl}
          routeLensEnabled={routeLensEnabled}
          dayCanvasTravelBuffer={dayCanvasTravelBuffer}
          dayCanvasDispatcher={dayCanvasDispatcher}
          setAnchorDate={setAnchorDate}
          setDate={setDate}
          setZoom={setZoom}
          handleDayClick={handleDayClick}
          onYearDateSelect={(dateIso) => {
            setDate(dateIso);
            setAnchorDate(new Date(`${dateIso}T12:00:00`));
            setZoom('day');
            setLens('calendar');
          }}
        />
        <DragOverlay dropAnimation={null}>
          {dragEntry && <DragOverlayCard entry={dragEntry} timePreview={timePreview} />}
        </DragOverlay>
      </DndContext>

      <AddToSchedulePanel isOpen={addPanel} onClose={() => setAddPanel(false)} onCreated={() => { refresh(); setToast({ msg: 'Added to schedule', type: 'success' }); }} preselect={addPreselect} />
      <EditEntryPanel entry={editEntry} users={users} onClose={() => setEditEntry(null)} onUpdated={refresh} onError={(msg) => setToast({ msg, type: 'error' })} />
      {isAppointmentCluster && (
        <AppointmentDetailPanel
          entry={detailEntry}
          operatorName={detailEntry?.assigned_to?.[0] ? (users.find(u => u.id === detailEntry.assigned_to?.[0])?.first_name ?? '') + ' ' + (users.find(u => u.id === detailEntry.assigned_to?.[0])?.last_name ?? '') : 'Unassigned'}
          onClose={() => setDetailEntry(null)}
          onAdvance={handleAppointmentAdvance}
        />
      )}
      {isAppointmentCluster && (
        <WalkInModal
          isOpen={walkInOpen} services={services} operators={users}
          entries={entries}
          // eslint-disable-next-line @typescript-eslint/no-floating-promises
          onBook={(data) => { handleAppointmentBook({ ...data, source: 'walk_in' satisfies BookingSource }); setWalkInOpen(false); }}
          onClose={() => setWalkInOpen(false)}
        />
      )}
      {toast && <Toast message={toast.msg} type={toast.type} isVisible={!!toast} onDismiss={() => setToast(null)} />}
      <ScheduleSearchSheet open={searchOpen} entries={entries} onClose={() => setSearchOpen(false)} onSelect={setDetailEntry} />
      <div
        style={{
          position: 'fixed',
          left: 16,
          right: 16,
          bottom: 14,
          display: 'flex',
          justifyContent: 'space-between',
          pointerEvents: 'none',
          zIndex: 30,
        }}
      >
        <div style={{ pointerEvents: 'auto' }}>
          <TodayPill onClick={goToToday} />
        </div>
        <button
          onClick={() => { setAddPreselect(null); setAddPanel(true); }}
          style={{
            pointerEvents: 'auto',
            display: 'flex',
            alignItems: 'center',
            gap: 6,
            padding: '10px 14px',
            borderRadius: 999,
            border: 'none',
            background: palette.accent,
            color: palette.white,
            cursor: 'pointer',
            fontWeight: 700,
            fontSize: 13,
            boxShadow: '0 8px 20px rgba(37,99,235,0.28)',
            fontFamily: 'inherit',
          }}
        >
          <Plus size={15} />
          {vocab.primaryAction}
        </button>
      </div>
      {isAppointmentCluster && <KeyboardShortcutHelp shortcuts={shortcuts} />}
    </div>
  );
}
